import { Debug } from '@/components/common/output/debug';
import type { Gender, Person } from '@/store/people/types';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import type { GroupTypeKey } from '@/shared/types/group';
import {
  FieldDisplay,
  type FieldInteractions,
  type PersonFieldKey,
} from './fields';
import { getFullName } from '@/shared/lib/names';
import type { Address } from '@/shared/types/address';

export function PersonModal({
  target,
  isOpen,
  onOpenChange,
  onSave: saveHandler,
  saving,
  fields,
  interaction,
}: {
  target: Person | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (item: Person) => void;
  saving: boolean;
  fields?: PersonFieldKey[];
  interaction?: FieldInteractions;
}) {
  const [editable, setEditable] = useState<Person | null>(null);

  const updateEditable = (data: Partial<Person['data']>) => {
    if (!target) {
      return;
    }

    const updated = {
      id: target?.id || '',
      data: {
        ...target.data,
        ...(editable?.data || {}),
        ...data,
      },
    };
    // computed values
    updated.data.fullname = getFullName(updated.data);
    setEditable(updated);
  };

  const onStringChange = (key: PersonFieldKey, value: string) => {
    updateEditable({ [key]: value });
  };

  const onGenderChange = (key: PersonFieldKey, value: Gender | undefined) => {
    if (key !== 'gender') {
      throw new Error(`Expected "gender" as field key but got "${key}"`);
    }

    updateEditable({ gender: value });
  };

  const onBirthdateChange = (
    key: PersonFieldKey,
    value: string | undefined,
  ) => {
    if (key !== 'birthdate') {
      throw new Error(`Expected "birthdate" as field key but got "${key}"`);
    }

    updateEditable({ birthdate: value });
  };

  const onCampusChange = (id: string) => {
    updateEditable({ campus_id: id });
  };

  const onLayGroupChange = (id: string, type: GroupTypeKey) => {
    const key = type;
    updateEditable({
      groups: {
        ...(target?.data.groups || {}),
        ...(editable?.data.groups || {}),
        [key]: id,
      },
    });
  };

  const onAddressChange = (address: Partial<Address>) => {
    updateEditable({ address: address as Address });
  };

  useEffect(() => {
    setEditable(isOpen ? target : null);
  }, [isOpen, target]);

  const fieldInfo = useMemo(() => {
    const keys = fields || [
      'firstname',
      'lastname',
      'fullname',
      'email',
      'phone',
      'address',
      'campus_id',
      'laygroup1',
      'laygroup2',
    ];

    return keys.map((key) => ({ key, interaction: interaction?.[key] }));
  }, [fields, interaction]);

  const onSave = () => {
    if (!editable) {
      return;
    }

    const updates: Person['data'] = {};
    for (const field of fieldInfo) {
      if (field.interaction?.isDisabled || field.interaction?.isReadOnly) {
        continue;
      }

      if (field.key === 'laygroup1' || field.key === 'laygroup2') {
        updates.groups = editable.data.groups;
      } else if (field.key === 'gender') {
        updates[field.key] = editable.data[field.key];
      } else if (field.key === 'marital_status') {
        updates[field.key] = editable.data[field.key];
      } else if (field.key === 'address') {
        updates.address = editable.data.address;
      } else {
        updates[field.key] = editable.data[field.key];
      }
    }

    saveHandler({ id: editable.id, data: updates });
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} scrollBehavior='inside'>
      <ModalContent>
        {(onClose) =>
          !target || !editable ? (
            <></>
          ) : (
            <>
              <ModalHeader>Edit Profile</ModalHeader>
              <ModalBody>
                {fieldInfo.map((info) => (
                  <FieldDisplay
                    key={info.key}
                    fieldKey={info.key}
                    editable={editable}
                    target={target}
                    interaction={info.interaction}
                    onStringChange={onStringChange}
                    onGenderChange={onGenderChange}
                    onBirthdateChange={onBirthdateChange}
                    onCampusChange={onCampusChange}
                    onLayGroupChange={onLayGroupChange}
                    onAddressChange={onAddressChange}
                  />
                ))}
                <Debug value={editable} />
              </ModalBody>
              <ModalFooter>
                <Button variant='flat' onPress={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant='solid'
                  color='primary'
                  onPress={onSave}
                  isLoading={saving}
                >
                  Update
                </Button>
              </ModalFooter>
            </>
          )
        }
      </ModalContent>
    </Modal>
  );
}
